import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Spin,
  Table,
  Tag,
  Progress,
  Typography,
  Space,
  message,
} from 'antd';
import {
  BankOutlined,
  DollarOutlined,
  EnvironmentOutlined,
  SafetyCertificateOutlined,
  WarningOutlined,
  ThunderboltOutlined,
  AuditOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import { API_SERVIDOR2 } from '../config/api';

const { Text } = Typography;

interface Produto {
  id: string;
  nome: string;
  descricao: string;
  total: number;
  cor: string;
  icone: string;
}

interface DashboardData {
  produtos: Produto[];
  total_geral: number;
  por_valor: Record<string, number>;
  por_uf: Record<string, number>;
  por_qualidade: Record<string, number>;
  atualizado_em: string;
}


const PRODUTO_ICONS: Record<string, React.ReactNode> = {
  novos: <SafetyCertificateOutlined />,
  risco: <WarningOutlined />,
  execucao: <ThunderboltOutlined />,
  antigas: <AuditOutlined />,
};

const COR_MAP: Record<string, string> = {
  blue: '#1890ff',
  orange: '#fa8c16',
  red: '#f5222d',
  green: '#52c41a',
};

const FAIXA_TEMPO_LABELS: Record<string, string> = {
  'ate_1ano': 'Até 1 ano',
  '1_3anos': '1-3 anos',
  '3_5anos': '3-5 anos',
  '5_10anos': '5-10 anos',
  'acima_10anos': 'Acima de 10 anos',
};

const FAIXA_VALOR_LABELS: Record<string, string> = {
  'ate_50k': 'Até R$ 50k',
  '50k_200k': 'R$ 50k - 200k',
  '200k_500k': 'R$ 200k - 500k',
  '500k_1M': 'R$ 500k - 1M',
  'acima_1M': 'Acima de R$ 1M',
};

const PGFNDashboardBI: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [dashData, setDashData] = useState<DashboardData | null>(null);
  const [selectedProduto, setSelectedProduto] = useState<string>('');
  const [selectedUf, setSelectedUf] = useState<string>('');

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const res = await axios.get(API_SERVIDOR2.PGFN_PRODUTOS_DASHBOARD);
      setDashData(res.data);
    } catch (error) {
      console.error('Erro ao carregar dashboard:', error);
      message.error('Erro ao carregar dashboard');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
  }, []);

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('pt-BR').format(num);
  };

  const formatCurrency = (num: number) => {
    if (num >= 1e9) return `R$ ${(num / 1e9).toFixed(2)} bi`;
    if (num >= 1e6) return `R$ ${(num / 1e6).toFixed(2)} mi`;
    if (num >= 1e3) return `R$ ${(num / 1e3).toFixed(0)} mil`;
    return `R$ ${num.toFixed(2)}`;
  };

  const getTopUFs = () => {
    if (!dashData?.por_uf) return [];
    const entries = Object.entries(dashData.por_uf);
    return entries.sort((a, b) => b[1] - a[1]).slice(0, 10);
  };

  const getProdutoPercent = (produto: Produto) => {
    if (!dashData?.total_geral) return 0;
    return (produto.total / dashData.total_geral) * 100;
  };

  if (loading && !dashData) {
    return (
      <div style={{ textAlign: 'center', padding: 100 }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>Carregando Dashboard BI...</div>
      </div>
    );
  }

  return (
    <div>
      {/* Header com total e qualidade */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={8}>
          <Card style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
            <Statistic
              title={<span style={{ color: 'rgba(255,255,255,0.85)' }}>Total Empresas</span>}
              value={dashData?.total_geral || 0}
              valueStyle={{ color: '#fff', fontSize: 28 }}
              prefix={<BankOutlined />}
              formatter={(v) => formatNumber(Number(v))}
            />
          </Card>
        </Col>
        <Col span={16}>
          <Card title="Qualidade dos Leads">
            <Row gutter={16}>
              {Object.entries(dashData?.por_qualidade || {}).map(([linha, count]) => {
                const colors: Record<string, string> = { A: '#52c41a', B: '#faad14', C: '#f5222d' };
                const labels: Record<string, string> = { A: 'Alto Potencial', B: 'Médio Potencial', C: 'Baixo Potencial' };
                return (
                  <Col span={8} key={linha}>
                    <Statistic
                      title={<Tag color={colors[linha]}>{linha} - {labels[linha]}</Tag>}
                      value={count}
                      formatter={(v) => formatNumber(Number(v))}
                      valueStyle={{ color: colors[linha] }}
                    />
                  </Col>
                );
              })}
            </Row>
          </Card>
        </Col>
      </Row>

      {/* Produtos PGFN */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        {dashData?.produtos.map((produto) => {
          const color = COR_MAP[produto.cor] || '#1890ff';
          const percent = getProdutoPercent(produto);
          const icon = PRODUTO_ICONS[produto.id] || <BankOutlined />;
          return (
            <Col span={6} key={produto.id}>
              <Card 
                hoverable
                style={{ 
                  borderTop: `4px solid ${color}`,
                  cursor: selectedProduto === produto.id ? 'default' : 'pointer',
                  background: selectedProduto === produto.id ? '#f0f5ff' : undefined,
                }}
                onClick={() => setSelectedProduto(selectedProduto === produto.id ? '' : produto.id)}
              >
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Space>
                    <span style={{ color, fontSize: 24 }}>{icon}</span>
                    <Text strong style={{ fontSize: 16 }}>{produto.icone} {produto.nome}</Text>
                  </Space>
                  <Statistic 
                    value={produto.total} 
                    formatter={(v) => formatNumber(Number(v))}
                    valueStyle={{ color }}
                  />
                  <Progress 
                    percent={percent} 
                    strokeColor={color}
                    format={(p) => `${p?.toFixed(1)}%`}
                    size="small"
                  />
                  <Text type="secondary" style={{ fontSize: 12 }}>{produto.descricao}</Text>
                </Space>
              </Card>
            </Col>
          );
        })}
      </Row>

      {/* Top UFs e Faixas de Valor */}
      <Row gutter={16}>
        <Col span={12}>
          <Card title={<><EnvironmentOutlined /> Top 10 Estados</>} style={{ height: 400 }}>
            <Table
              dataSource={getTopUFs().map(([ufCode, count]) => ({ uf: ufCode, count }))}
              columns={[
                { title: 'UF', dataIndex: 'uf', key: 'uf', width: 60 },
                { 
                  title: 'Empresas', 
                  dataIndex: 'count', 
                  key: 'count',
                  render: (v) => formatNumber(v),
                },
                {
                  title: '%',
                  key: 'percent',
                  render: (_, record) => (
                    <Progress 
                      percent={(record.count / (dashData?.total_geral || 1)) * 100} 
                      size="small"
                      format={(p) => `${p?.toFixed(1)}%`}
                    />
                  ),
                },
              ]}
              pagination={false}
              size="small"
              rowKey="uf"
              onRow={(record) => ({
                onClick: () => setSelectedUf(selectedUf === record.uf ? '' : record.uf),
                style: { cursor: 'pointer', background: selectedUf === record.uf ? '#e6f7ff' : undefined },
              })}
            />
          </Card>
        </Col>
        <Col span={12}>
          <Card title={<><DollarOutlined /> Por Faixa de Valor</>} style={{ height: 400 }}>
            <Table
              dataSource={Object.entries(dashData?.por_valor || {}).map(([faixa, count]) => ({ 
                faixa, 
                label: FAIXA_VALOR_LABELS[faixa] || faixa,
                count 
              }))}
              columns={[
                { title: 'Faixa', dataIndex: 'label', key: 'label' },
                { 
                  title: 'Empresas', 
                  dataIndex: 'count', 
                  key: 'count',
                  render: (v) => formatNumber(v),
                },
                {
                  title: '%',
                  key: 'percent',
                  render: (_, record) => (
                    <Progress 
                      percent={(record.count / (dashData?.total_geral || 1)) * 100} 
                      size="small"
                      format={(p) => `${p?.toFixed(1)}%`}
                      strokeColor={
                        record.faixa === 'acima_1M' ? '#722ed1' :
                        record.faixa === '500k_1M' ? '#1890ff' :
                        '#52c41a'
                      }
                    />
                  ),
                },
              ]}
              pagination={false}
              size="small"
              rowKey="faixa"
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default PGFNDashboardBI;
